import {
  OrganizationListResponseDto,
} from "@/lib/api/interfaces/internal.interface";
import { PaginatedInfo } from "@/lib/api/interfaces/utils";
import {
  useOrganizationsInternalFindInitialQuery,
  UseOrganizationsInternalFindInitialParams,
} from "@/lib/api/requests/internal.requests";
import { formatDate } from "@/lib/utils/date-formatter";
import { usePageState } from "@/lib/hooks/use-page-state";
import { Badge } from "@/ui-components/ui/badge";
import { Button } from "@/ui-components/ui/button";
import { toast } from "@/lib/toast";
import {
  DataTable2,
  DataTable2ColumnHeader,
  DataTable2Pagination,
  DataTable2Toolbar,
  getDataTable2SelectionColumn,
  useDataTable2,
} from "@/util-components/data-table-2/components";
import { AdvancedFilterSortPanel } from "@/util-components/data-view/components/advanced-filter-sort-panel";
import {
  AdvancedFilterDef,
  AdvancedSortDef,
  AdvancedState,
} from "@/util-components/data-view/components/advanced-filter-types";
import { PageHeader, PageLayout, PageTitle } from "@/util-components/page-layout/page-layout";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { Copy, Shield } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// --- Settings type for usePageState ---
interface VereinePageSettings {
  advancedState: AdvancedState;
  columnVisibility: VisibilityState;
}

const DEFAULT_ADVANCED_STATE: AdvancedState = {
  filters: [],
  sorts: [{ key: "name", direction: "asc" }],
};

const DEFAULT_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 20;

// --- Filter & Sort Definitions ---
const FILTER_DEFS: AdvancedFilterDef[] = [
  {
    key: "isBanned",
    label: "Ban-Status",
    type: "select",
    options: [
      { value: "true", label: "Gesperrt" },
      { value: "false", label: "Aktiv" },
    ],
  },
  {
    key: "planKey",
    label: "Subscription-Plan",
    type: "multi-select",
    options: [
      { value: "basic", label: "Basic" },
      { value: "pro", label: "Pro" },
      { value: "custom", label: "Custom" },
      { value: "none", label: "Kein Plan" },
    ],
  },
  {
    key: "subscriptionStatus",
    label: "Subscription-Status",
    type: "multi-select",
    options: [
      { value: "trialing", label: "Testphase" },
      { value: "active", label: "Aktiv" },
      { value: "grace_period", label: "Kulanzzeit" },
      { value: "past_due", label: "Überfällig" },
      { value: "canceled", label: "Gekündigt" },
      { value: "expired", label: "Abgelaufen" },
      { value: "inactive", label: "Inaktiv" },
    ],
  },
];

const SORT_DEFS: AdvancedSortDef[] = [
  { key: "name", label: "Name" },
  { key: "banReason", label: "Ban-Status" },
  { key: "subscriptionPlan", label: "Subscription-Plan" },
  { key: "subscriptionStatus", label: "Abo-Status" },
  { key: "createdAt", label: "Erstellt am" },
  { key: "lastLogin", label: "Zuletzt aktiv" },
];

// --- Plan/Status Display Helpers ---
const SOFT_BADGE_BASE =
  "rounded-full px-2.5 py-0.5 text-xs font-medium shadow-none border-none hover:bg-opacity-100 hover:opacity-100 pointer-events-none";

const PLAN_CONFIG: Record<string, { label: string; className: string }> = {
  basic: {
    label: "Basic",
    className: `${SOFT_BADGE_BASE} bg-blue-100 text-blue-700`,
  },
  pro: {
    label: "Pro",
    className: `${SOFT_BADGE_BASE} bg-violet-100 text-violet-700`,
  },
  custom: {
    label: "Custom",
    className: `${SOFT_BADGE_BASE} bg-slate-100 text-slate-700`,
  },
};

const STATUS_CONFIG: Record<
  string,
  { label: string; className?: string; variant?: "default" | "secondary" | "destructive" | "outline" }
> = {
  trialing: {
    label: "Testphase",
    className: `${SOFT_BADGE_BASE} bg-amber-100 text-amber-700`,
  },
  active: {
    label: "Aktiv",
    className: `${SOFT_BADGE_BASE} bg-emerald-100 text-emerald-700`,
  },
  grace_period: {
    label: "Kulanzzeit",
    className: `${SOFT_BADGE_BASE} bg-yellow-100 text-yellow-700`,
  },
  past_due: {
    label: "Überfällig",
    className: `${SOFT_BADGE_BASE} bg-red-100 text-red-700`,
  },
  canceled: {
    label: "Gekündigt",
    className: `${SOFT_BADGE_BASE} bg-slate-100 text-slate-700`,
  },
  expired: {
    label: "Abgelaufen",
    className: `${SOFT_BADGE_BASE} bg-slate-100 text-slate-700`,
  },
  inactive: {
    label: "Inaktiv",
    className: `${SOFT_BADGE_BASE} bg-slate-100 text-slate-700`,
  },
};

const copyToClipboard = async (value: string, successMessage: string) => {
  await navigator.clipboard.writeText(value);
  toast.success(successMessage);
};

// --- Columns ---
const getColumns = (): ColumnDef<OrganizationListResponseDto>[] => {
  return [
    getDataTable2SelectionColumn<OrganizationListResponseDto>(),
    {
      accessorKey: "name",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Name" />,
      meta: { label: "Name" },
    },
    {
      accessorKey: "banReason",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Status" />,
      meta: { label: "Ban-Status" },
      cell: ({ row }) =>
        row.original.isOpenable ? (
          <Badge
            className={
              row.original.banReason !== "NONE"
                ? `${SOFT_BADGE_BASE} bg-red-100 text-red-700`
                : `${SOFT_BADGE_BASE} bg-emerald-100 text-emerald-700`
            }
          >
            {row.original.banReason !== "NONE" ? "Gesperrt" : "Aktiv"}
          </Badge>
        ) : (
          <Badge className={`${SOFT_BADGE_BASE} bg-red-100 text-red-700`}>Gesperrt</Badge>
        ),
    },
    {
      accessorKey: "organizationAdminEmail",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Admin" />,
      meta: { label: "Admin" },
      cell: ({ row }) => {
        const adminEmail = row.original.organizationAdmin?.mail;
        const adminName = row.original.organizationAdmin 
          ? `${row.original.organizationAdmin.firstName || ''} ${row.original.organizationAdmin.lastName || ''}`.trim()
          : null;

        if (!adminEmail) {
          return <span className="text-gray-400 text-sm">—</span>;
        }

        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col min-w-0">
              {adminName ? (
                <span className="text-sm font-medium text-slate-900 truncate">
                  {adminName}
                </span>
              ) : null}
              <span className="text-xs text-slate-500 truncate">
                {adminEmail}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100/50 transition-colors"
              onClick={async (event) => {
                event.stopPropagation();
                await copyToClipboard(adminEmail, "Admin-E-Mail kopiert");
              }}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: "subscriptionPlan",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Plan" />,
      meta: { label: "Subscription-Plan" },
      cell: ({ row }) => {
        const plan = row.original.subscriptionPlan;
        if (!plan) return <span className="text-gray-400 text-sm">—</span>;
        const config = PLAN_CONFIG[plan];
        if (!config) return <Badge variant="outline">{plan}</Badge>;
        return <Badge className={config.className}>{config.label}</Badge>;
      },
    },
    {
      accessorKey: "subscriptionStatus",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Abo-Status" />,
      meta: { label: "Abo-Status" },
      cell: ({ row }) => {
        const status = row.original.subscriptionStatus;
        if (!status) return <span className="text-gray-400 text-sm">—</span>;
        const config = STATUS_CONFIG[status];
        if (!config) return <Badge className={`${SOFT_BADGE_BASE} bg-slate-100 text-slate-700`}>{status}</Badge>;
        return (
          <Badge variant={config.variant} className={config.className}>
            {config.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Erstellt am" />,
      meta: { label: "Erstellt am" },
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      accessorKey: "lastLogin",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Zuletzt aktiv" />,
      meta: { label: "Zuletzt aktiv" },
      cell: ({ row }) => formatDate(row.original.lastLogin),
    },
  ];
};

// --- Main Page ---
const InternalTeamsWrapper = () => {
  return (
    <PageLayout basePath="/vereine" className="bg-white">
      <InternalTeams />
    </PageLayout>
  );
};

const InternalTeams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // --- usePageState for persistence ---
  const { state: pageState, setSettings } = usePageState<VereinePageSettings>("internal-vereine", {
    settings: {
      advancedState: DEFAULT_ADVANCED_STATE,
      columnVisibility: {},
    },
  });

  const advancedState: AdvancedState = useMemo(
    () => pageState.settings?.advancedState || DEFAULT_ADVANCED_STATE,
    [pageState.settings?.advancedState],
  );

  const persistedColumnVisibility: VisibilityState = useMemo(
    () => pageState.settings?.columnVisibility || {},
    [pageState.settings?.columnVisibility],
  );

  const initialUiTextSearch = searchParams.get("textSearch") ?? "";
  const [uiTextSearch, setUiTextSearch] = useState<string>(initialUiTextSearch);
  const initialPage = Number.parseInt(searchParams.get("page") ?? "1", 10);

  // --- Build API params from advancedState ---
  const params = useMemo<UseOrganizationsInternalFindInitialParams>(() => {
    const sort = advancedState.sorts[0];
    const isBannedFilter = advancedState.filters.find((f) => f.key === "isBanned");
    const planKeyFilter = advancedState.filters.find((f) => f.key === "planKey");
    const subStatusFilter = advancedState.filters.find((f) => f.key === "subscriptionStatus");

    return {
      limit: DEFAULT_PAGE_SIZE,
      page: Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1,
      ...(uiTextSearch.trim().length >= 4 ? { textSearch: uiTextSearch.trim() } : {}),
      ...(isBannedFilter?.value !== undefined && isBannedFilter?.value !== ""
        ? { isBanned: isBannedFilter.value === "true" || isBannedFilter.value === true }
        : {}),
      ...(Array.isArray(planKeyFilter?.value) && planKeyFilter.value.length > 0
        ? { planKey: planKeyFilter.value }
        : {}),
      ...(Array.isArray(subStatusFilter?.value) && subStatusFilter.value.length > 0
        ? { subscriptionStatus: subStatusFilter.value }
        : {}),
      ...(sort ? { sortBy: sort.key, sortDirection: sort.direction } : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedState, uiTextSearch, initialPage]);

  const [currentParams, setCurrentParams] = useState(params);

  // Sync params changes
  useEffect(() => {
    setCurrentParams((prev) => {
      // Keep the same page unless filters changed
      const filtersChanged =
        prev.textSearch !== params.textSearch ||
        prev.isBanned !== params.isBanned ||
        JSON.stringify(prev.planKey) !== JSON.stringify(params.planKey) ||
        JSON.stringify(prev.subscriptionStatus) !== JSON.stringify(params.subscriptionStatus) ||
        prev.sortBy !== params.sortBy ||
        prev.sortDirection !== params.sortDirection;

      return filtersChanged ? { ...params, page: 1 } : params;
    });
  }, [params]);

  const navigate = useNavigate();

  useEffect(() => {
    setSearchParams(
      {
        page: (currentParams.page ?? 1).toString(),
        ...(currentParams.textSearch ? { textSearch: currentParams.textSearch } : {}),
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParams]);

  const { data: response, isLoading, isFetching, isError } = useOrganizationsInternalFindInitialQuery(currentParams, {
    placeholderData: (previousData) => previousData,
    refetchOnMount: "always",
  });

  const organizations = response?.organizations?.docs ?? [];
  const paginatedInfo: PaginatedInfo | undefined = response?.organizations;

  const columns = useMemo(() => getColumns(), []);

  // --- Table with manual sorting ---
  const { table, columnVisibility, sorting, setSorting } = useDataTable2({
    data: organizations,
    columns,
    enableRowSelection: true,
    getRowId: (row) => row._id,
    manualSorting: true,
    initialColumnVisibility: persistedColumnVisibility,
    initialSorting: advancedState.sorts.length > 0
      ? [{ id: advancedState.sorts[0].key, desc: advancedState.sorts[0].direction === "desc" }]
      : [],
  });

  // Sync column visibility changes to page state
  useEffect(() => {
    setSettings({ columnVisibility });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnVisibility]);

  // Sync table header sort → advanced state (bidirectional)
  useEffect(() => {
    if (sorting.length > 0) {
      const newSort = { key: sorting[0].id, direction: (sorting[0].desc ? "desc" : "asc") as "asc" | "desc" };
      const currentSort = advancedState.sorts[0];
      if (!currentSort || currentSort.key !== newSort.key || currentSort.direction !== newSort.direction) {
        setSettings({ advancedState: { ...advancedState, sorts: [newSort] } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  const handleAdvancedStateChange = useCallback(
    (newState: AdvancedState) => {
      setSettings({ advancedState: newState });
      // Sync sort → table sorting state
      if (newState.sorts.length > 0) {
        const s = newState.sorts[0];
        setSorting([{ id: s.key, desc: s.direction === "desc" }]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSettings, setSorting],
  );

  const handleAdvancedReset = useCallback(() => {
    setSettings({ advancedState: DEFAULT_ADVANCED_STATE });
    setSorting([{ id: "name", desc: false }]);
    setUiTextSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSettings, setSorting]);

  const selectedCount = table.getSelectedRowModel().rows.length;
  const rowCount = table.getRowModel().rows.length;
  const isTableLoading = isLoading || isFetching;
  const hasError = isError && organizations.length === 0;

  return (
    <div className="h-full flex flex-col">
      <PageHeader>
        <PageTitle title="Vereine" icon={Shield} iconColor="text-blue-600" />
      </PageHeader>

      <div className="flex-1 overflow-y-auto w-full">
        <div className="max-w-7xl mx-auto space-y-6 py-6">
          <div className="bg-white overflow-hidden">
            <DataTable2Toolbar
              className="px-6"
              table={table}
              searchQuery={uiTextSearch}
              onSearchChange={(text) => {
                setUiTextSearch(text);
                const normalized = text.trim();
                setCurrentParams((prev) => {
                  if (normalized.length === 0) {
                    return { ...prev, textSearch: undefined, page: 1 };
                  }

                  if (normalized.length >= 4) {
                    return { ...prev, textSearch: normalized, page: 1 };
                  }

                  return prev;
                });
              }}
              searchHint={
                uiTextSearch.trim().length > 0 && uiTextSearch.trim().length < 4 ? "Mindestens 4 Zeichen" : undefined
              }
              trailing={
                <div className="flex items-center gap-2">
                  {selectedCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 gap-2 shadow-sm font-medium text-slate-700 bg-white border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                      onClick={async () => {
                        const selectedRows = table.getSelectedRowModel().rows;
                        const emails = selectedRows
                          .map((r) => r.original.organizationAdmin?.mail)
                          .filter(Boolean)
                          .join("; ");
                        
                        if (emails) {
                          await copyToClipboard(emails, `${selectedRows.length} E-Mails für Outlook kopiert`);
                        } else {
                          toast.error("Keine E-Mails zum Kopieren gefunden");
                        }
                      }}
                    >
                      <Copy className="h-4 w-4 text-slate-400" />
                      E-Mails kopieren ({selectedCount})
                    </Button>
                  )}
                  <AdvancedFilterSortPanel
                    filterDefs={FILTER_DEFS}
                    sortDefs={SORT_DEFS}
                    state={advancedState}
                    onChange={handleAdvancedStateChange}
                    onReset={handleAdvancedReset}
                    defaultSort={{ key: "name", direction: "asc" }}
                  />
                </div>
              }
            />

            <div className="px-6 pb-4 pt-4">
              <DataTable2
                table={table}
                loading={isTableLoading}
                loadingText="Vereine werden geladen..."
                minHeight={420}
                onRowClick={(row) => navigate(`/vereine/${row._id}`)}
                emptyTitle={hasError ? "Vereine konnten nicht geladen werden" : "Keine Vereine gefunden"}
                emptyDescription={
                  hasError
                    ? "Bitte versuche es erneut oder reduziere die Filter."
                    : "Passe deine Suche an oder versuche es später erneut."
                }
              />
            </div>

            <div className="border-t border-gray-100 px-6 py-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-gray-500">
                  {selectedCount > 0 ? `${selectedCount} von ${rowCount} ausgewählt.` : null}
                </div>
                {paginatedInfo ? (
                  <DataTable2Pagination
                    currentPage={currentParams.page ?? 1}
                    pageCount={paginatedInfo.pageCount ?? 0}
                    hasNextPage={paginatedInfo.hasNextPage ?? false}
                    hasPrevPage={paginatedInfo.hasPrevPage ?? false}
                    onPageChange={(page) => setCurrentParams((prev) => ({ ...prev, page }))}
                    pageSize={currentParams.limit ?? 10}
                    pageSizeOptions={[10, 20]}
                    onPageSizeChange={(limit) =>
                      setCurrentParams((prev) => ({
                        ...prev,
                        limit: Math.min(limit, MAX_PAGE_SIZE),
                        page: 1,
                      }))
                    }
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalTeamsWrapper;
